<script setup lang="ts">
import { computed, onBeforeMount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import { useI18n } from 'vue-i18n'
import { useLocalStorage } from '@vueuse/core/index'
import dayjs from 'dayjs'

import { LoadingList, RouteDraft } from '@/types/cargo-loading'
import { useAuthStore } from '@/stores/auth-store'
import { useFileDownloader } from '@/hooks/use-file-downloader'
import { ResourceResponse } from '@/types/general'
import { useMittListener } from '@/hooks/use-mitt-listener'
import { MyButtonScheme } from '@/types/layout/my-button'
import { useConfirm } from '@/hooks/use-confirm'

import MyModal from '@/components/my-components/MyModal.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import LoaderWrapper from '@/components/loaders/LoaderWrapper.vue'
import LoadingListContent from '@/components/cargo-loading/LoadingListContent.vue'

const props = defineProps<{
    loadingListId?: string
}>()

const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()
const confirm = useConfirm()
const { downloading, downloadFile } = useFileDownloader()
const { t } = useI18n()
const loadingListRouteDrafts = useLocalStorage<RouteDraft>('loadingListRouteDrafts', {})

const loading = ref(false)
const loadingList = ref<LoadingList | null>(null)
const isEditing = ref(!!route.query['is-editing'])
const hasValidDraft = ref(false)

const downloadRoute = computed(() => {
    return window.route('cl.loading-lists.download-single', {
        loading_list: loadingList.value?.id,
    })
})

async function fetchLoadingList() {
    const listId = props.loadingListId ?? route.params.listId

    if (!listId) return

    loading.value = true
    try {
        const response = await axios.get<ResourceResponse<LoadingList>>(
            window.route('cl.loading-lists.show', {
                loading_list: listId,
            }),
        )

        loadingList.value = response.data.data
        checkForDraft()
    } finally {
        loading.value = false
    }
}

function checkForDraft() {
    const listId = props.loadingListId ?? (route.params.listId as string)
    if (!listId) return

    hasValidDraft.value = !!loadingListRouteDrafts.value[listId]
}

function deleteOldDrafts() {
    const oneWeekAgo = dayjs().subtract(1, 'week')

    Object.keys(loadingListRouteDrafts.value).forEach((key) => {
        const draft = loadingListRouteDrafts.value[key]
        if (dayjs(draft.timestamp).isBefore(oneWeekAgo)) {
            delete loadingListRouteDrafts.value[key]
        }
    })
}

function onLoadingListSaved() {
    isEditing.value = false
    delete loadingListRouteDrafts.value[loadingList.value!.id]
}

async function onClose() {
    if (isEditing.value) {
        await showDeleteDraftPrompt()
    }

    router.replace({ name: 'cl.company.loading-lists.index', query: route.query })
}

async function onCancel() {
    await showDeleteDraftPrompt()
    isEditing.value = false
    // Refresh the loading list to get the old data
    await fetchLoadingList()
}

async function showDeleteDraftPrompt() {
    checkForDraft()
    if (!hasValidDraft.value) {
        return
    }

    try {
        await confirm(t('saveEntityTitle', { entity: t('draft') }), t('saveDraftPrompt'), {
            confirmText: t('saveDraft'),
            cancelText: t('deleteDraft'),
            confirmButtonScheme: MyButtonScheme.Primary,
            forceChoice: true,
        })
    } catch (e) {
        delete loadingListRouteDrafts.value[loadingList.value!.id]
    }
}

watch(() => props.loadingListId, fetchLoadingList)

useMittListener('fetchLoadingLists', fetchLoadingList)
onBeforeMount(() => {
    deleteOldDrafts()
    fetchLoadingList()
})
onMounted(() => {
    // Remove the is-editing when mounted so it isn't active on next modal open
    if (route.query['is-editing']) {
        const query = { ...route.query }
        delete query['is-editing']

        router.replace({ query })
    }
})
</script>

<template>
    <MyModal :value="true" :max-width="1200" @close="onClose">
        <LoaderWrapper :visible="loading || downloading" />
        <div class="flex items-center float-right space-x-2">
            <MyButton
                v-if="authStore.companyId"
                plain
                icon
                size="small"
                scheme="light"
                @click="() => downloadFile(downloadRoute)"
            >
                <mdi:printer />
            </MyButton>

            <MyButton
                v-if="!isEditing"
                v-tooltip="hasValidDraft ? t('draftAvailable') : null"
                class="print:hidden"
                scheme="primary"
                size="small"
                @click="isEditing = true"
            >
                <span
                    v-if="hasValidDraft && !isEditing"
                    class="bg-yellow-500 animate-pulse aspect-square w-2 rounded-full mr-2"
                />
                {{ t('editRoutes') }}
            </MyButton>
        </div>

        <LoadingListContent
            v-if="loadingList"
            v-model:is-editing="isEditing"
            :loading-list="loadingList"
            @cancel="onCancel()"
            @save="onLoadingListSaved()"
        />
        <h1
            v-else-if="!loading"
            class="my-auto text-center text-4xl font-bold"
            v-text="t('entityNotFound', { entity: t('loadingList') })"
        />
    </MyModal>
</template>
